import React, { useState, useEffect, useRef } from 'react';
import './GeometricCharacters.css';

export default function GeometricCharacters({ emotion = 'neutral' }) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [containerEmotion, setContainerEmotion] = useState(emotion);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const containerRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const lastAngleRef = useRef(0);
  const rotationCountRef = useRef(0); // Conta rotações completas (360°)
  const dizzyTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const speedThresholdRef = useRef(20); // pixels per frame - detecta movimento rápido

  useEffect(() => {
    const handleMouseMove = (e) => {
      // Se o usuário está digitando, não ativa o efeito de tonto
      if (isUserTyping) {
        return;
      }

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newX = e.clientX - rect.left;
        const newY = e.clientY - rect.top;

        // Só atualiza a posição do mouse se NÃO estiver no estado dizzy
        // Isso evita re-renders que podem interromper a animação da espiral
        if (containerEmotion !== 'dizzy') {
          setMousePos({
            x: newX,
            y: newY,
          });
        }

        // Calcula velocidade do mouse
        const deltaX = newX - lastPosRef.current.x;
        const deltaY = newY - lastPosRef.current.y;
        const speed = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // Se o mouse está se movendo rápido, conta mudanças de direção circular
        if (speed > speedThresholdRef.current) {
          const currentAngle = Math.atan2(deltaY, deltaX);
          let angleDiff = currentAngle - lastAngleRef.current;

          // Normaliza a diferença de ângulo para -π a π
          if (angleDiff > Math.PI) {
            angleDiff -= 2 * Math.PI;
          } else if (angleDiff < -Math.PI) {
            angleDiff += 2 * Math.PI;
          }

          // Detecta mudança de ângulo significativa (30 graus = Math.PI / 6)
          // Isso indica movimento circular
          if (Math.abs(angleDiff) > Math.PI / 6) {
            rotationCountRef.current += 1;

            // Após 8 mudanças de ângulo rápidas e significativas = movimento circular
            if (rotationCountRef.current >= 8) {
              setContainerEmotion('dizzy');

              // Limpa timeout anterior se existir
              if (dizzyTimeoutRef.current) {
                clearTimeout(dizzyTimeoutRef.current);
              }

              // Volta ao normal após 1.5 segundos
              dizzyTimeoutRef.current = setTimeout(() => {
                setContainerEmotion(emotion);
                rotationCountRef.current = 0;
              }, 1500);
            }
          }

          lastAngleRef.current = currentAngle;
        }

        lastPosRef.current = { x: newX, y: newY };
      }
    };    // Listener para quando o usuário começa a digitar
    const handleInputFocus = () => {
      setIsUserTyping(true);
      // Se estava "tonto", volta ao normal
      if (containerEmotion === 'dizzy') {
        setContainerEmotion(emotion);
        rotationCountRef.current = 0;
        if (dizzyTimeoutRef.current) {
          clearTimeout(dizzyTimeoutRef.current);
        }
      }
    };

    const handleInputBlur = () => {
      // Aguarda um pouco para confirmar que o usuário parou de digitar
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsUserTyping(false);
      }, 500);
    };

    // Listener para detectar quando o usuário está digitando em qualquer input
    const handleInputChange = () => {
      setIsUserTyping(true);
      // Reset do timeout de digitação
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsUserTyping(false);
      }, 500);
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Adiciona listeners em todos os inputs
    const inputs = document.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', handleInputFocus);
      input.addEventListener('blur', handleInputBlur);
      input.addEventListener('input', handleInputChange);
    });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      inputs.forEach(input => {
        input.removeEventListener('focus', handleInputFocus);
        input.removeEventListener('blur', handleInputBlur);
        input.removeEventListener('input', handleInputChange);
      });
      if (dizzyTimeoutRef.current) {
        clearTimeout(dizzyTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [emotion, isUserTyping, containerEmotion]);

  // Calcula o ângulo entre um ponto e o mouse
  const calculateEyePosition = (eyeX, eyeY) => {
    const angle = Math.atan2(mousePos.y - eyeY, mousePos.x - eyeX);
    const distance = 6; // Distância que a pupila se move

    return {
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
    };
  };

  // Posições dos personagens - Formados como um grupo olhando
  const character1EyesPos = [
    { x: 80, y: 100 },
    { x: 130, y: 100 },
  ];

  const character2EyesPos = [
    { x: 300, y: 150 },
    { x: 350, y: 150 },
  ];

  const character3EyesPos = [
    { x: 200, y: 280 },
    { x: 250, y: 280 },
  ];

  const character4EyesPos = [
    { x: 420, y: 220 },
    { x: 460, y: 220 },
  ];

  // Usa containerEmotion se estiver "dizzy", caso contrário usa a emoção da prop
  const activeEmotion = containerEmotion === 'dizzy' ? containerEmotion : emotion;

  const getEyeRotation = (emotionType) => {
    switch (emotionType) {
      case 'surprise': // Olhando para cima com surpresa
        return 'surprise';
      case 'shy': // Olhando para o lado (tímido)
        return 'shy';
      case 'happy': // Normal mas feliz
        return 'normal';
      case 'sad': // Olhando para baixo
        return 'sad';
      case 'love': // Olhos de coração
        return 'love';
      case 'cool': // Óculos de sol
        return 'cool';
      case 'dizzy': // Tonto
        return 'dizzy';
      case 'laughing': // Gargalhada
        return 'laughing';
      case 'dead': // Morto de rir
        return 'dead';
      case 'confused': // Confuso
        return 'confused';
      case 'suspicious': // Desconfiado
        return 'suspicious';
      default:
        return 'normal';
    }
  };

  const getMouthExpression = (emotion) => {
    switch (emotion) {
      case 'surprise':
        return 'O'; // Boca redonda de surpresa
      case 'shy':
        return 'shy'; // Boca tímida
      case 'happy':
        return 'smile'; // Sorriso
      case 'sad':
        return 'sad'; // Boca triste
      case 'love':
        return 'heartSmile'; // Boca de coração
      case 'cool':
        return 'coolSmirk'; // Sorriso de malandro
      case 'dizzy':
        return 'confused'; // Boca confusa
      case 'laughing':
        return 'bigSmile'; // Grande sorriso
      case 'dead':
        return 'veryBigSmile'; // Sorriso gigante
      case 'confused':
        return 'confused'; // Confuso
      case 'suspicious':
        return 'suspicious'; // Desconfiado
      default:
        return 'neutral'; // Neutral
    }
  };

  const EyeComponent = React.memo(({ position, emotion, characterId }) => {
    const eyeMove = calculateEyePosition(position.x, position.y);
    const eyeRotation = getEyeRotation(emotion);

    let eyeOffsetX = eyeMove.x;
    let eyeOffsetY = eyeMove.y;

    // Ajusta o movimento dos olhos baseado na emoção
    if (eyeRotation === 'surprise') {
      eyeOffsetY = -8; // Olhando para cima
    } else if (eyeRotation === 'shy') {
      eyeOffsetX = -8; // Olhando para o lado
    } else if (eyeRotation === 'sad') {
      eyeOffsetY = 8; // Olhando para baixo
    } else if (eyeRotation === 'love') {
      eyeOffsetX = 0;
      eyeOffsetY = 0; // Olhos fixos
    } else if (eyeRotation === 'cool') {
      eyeOffsetX = 0;
      eyeOffsetY = 0; // Olhos fixos
    } else if (eyeRotation === 'dizzy') {
      eyeOffsetX = 0;
      eyeOffsetY = 0; // Olhos fixos
    } else if (eyeRotation === 'laughing') {
      eyeOffsetX = 0;
      eyeOffsetY = 5; // Olhando para baixo rindo
    } else if (eyeRotation === 'dead') {
      eyeOffsetX = 0;
      eyeOffsetY = 0; // Olhos fixos
    } else if (eyeRotation === 'confused') {
      // Cada olho olha para um lado
      eyeOffsetX = characterId % 2 === 0 ? -6 : 6;
      eyeOffsetY = 0;
    } else if (eyeRotation === 'suspicious') {
      eyeOffsetX = 6;
      eyeOffsetY = -3; // Olhando para o lado e para cima
    }

    // Renderizar diferentes tipos de olhos
    if (eyeRotation === 'love') {
      return (
        <div className="eye eye-love">
          <svg viewBox="0 0 40 40" className="eye-special">
            <path d="M 10 15 Q 20 5 30 15 Q 20 25 10 15" fill="currentColor" />
          </svg>
        </div>
      );
    }

    if (eyeRotation === 'cool') {
      return (
        <div className="eye eye-cool">
          <div className="sunglasses"></div>
        </div>
      );
    }

    if (eyeRotation === 'dizzy') {
      return (
        <div className="eye eye-dizzy">
          <svg viewBox="0 0 40 40" className="eye-special">
            {/* Espiral perfeita suave */}
            <path
              d="M 20 6 
                 Q 26 6 30 10
                 Q 34 14 34 20
                 Q 34 28 28 34
                 Q 20 40 12 34
                 Q 8 30 8 22
                 Q 8 16 12 12
                 Q 18 8 22 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }

    if (eyeRotation === 'dead') {
      return (
        <div className="eye eye-dead">
          <svg viewBox="0 0 40 40" className="eye-special">
            <line x1="12" y1="12" x2="28" y2="28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="28" y1="12" x2="12" y2="28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>
      );
    }

    // Olhos normais com movimento
    return (
      <div className="eye">
        <div className="eye-white">
          <div
            className="pupil"
            style={{
              transform: `translate(${eyeOffsetX}px, ${eyeOffsetY}px)`,
            }}
          />
          <div className="pupil-shine" />
        </div>
      </div>
    );
  });

  const MouthComponent = ({ emotion, characterId }) => {
    const mouthType = getMouthExpression(emotion);

    return (
      <div className={`mouth mouth-${mouthType}`}>
        {mouthType === 'O' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <circle cx="20" cy="20" r="10" fill="none" stroke="currentColor" strokeWidth="2.5" />
          </svg>
        )}
        {mouthType === 'smile' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <path d="M 10 18 Q 20 28 30 18" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        )}
        {mouthType === 'sad' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <path d="M 10 28 Q 20 18 30 28" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        )}
        {mouthType === 'shy' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <line x1="14" y1="20" x2="26" y2="20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        )}
        {mouthType === 'neutral' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <line x1="12" y1="20" x2="28" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
        {mouthType === 'heartSmile' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <path d="M 10 18 Q 20 28 30 18" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M 12 12 Q 8 8 5 12 Q 5 15 12 18 Q 19 15 19 12 Q 19 8 15 8 Q 12 8 12 10 Z" fill="currentColor" />
            <path d="M 28 12 Q 24 8 21 12 Q 21 15 28 18 Q 35 15 35 12 Q 35 8 31 8 Q 28 8 28 10 Z" fill="currentColor" />
          </svg>
        )}
        {mouthType === 'coolSmirk' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <path d="M 12 22 Q 20 26 30 18" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        )}
        {mouthType === 'confused' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            {/* Boca de tonto com ondas */}
            <path d="M 12 22 Q 14 18 16 22 Q 18 26 20 22 Q 22 18 24 22 Q 26 26 28 22" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {mouthType === 'bigSmile' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <path d="M 8 16 Q 20 32 32 16" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M 10 16 L 30 16" stroke="currentColor" fill="none" strokeWidth="1.5" />
          </svg>
        )}
        {mouthType === 'veryBigSmile' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <path d="M 5 14 Q 20 35 35 14" stroke="currentColor" fill="none" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M 8 14 L 32 14" stroke="currentColor" fill="none" strokeWidth="2" />
            <circle cx="40" cy="24" r="2" fill="currentColor" opacity="0.6" />
            <circle cx="0" cy="24" r="2" fill="currentColor" opacity="0.6" />
          </svg>
        )}
        {mouthType === 'suspicious' && (
          <svg viewBox="0 0 40 40" className="mouth-svg">
            <line x1="12" y1="20" x2="28" y2="22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M 28 22 L 30 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </div>
    );
  };

  return (
    <div className="characters-container" ref={containerRef}>
      {/* Sun - Sol Sorrindo Atrás */}
      <div className="sun">
        <div className="sun-rays">
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
          <div className="sun-ray"></div>
        </div>
        <div className="sun-eyes">
          <div className="sun-eye"></div>
          <div className="sun-eye"></div>
        </div>
        <div className="sun-smile"></div>
      </div>

      {/* Character 1 - Semicircle (Laranja) - Canto Esquerdo */}
      <div className="character character-1">
        <div className={`character-body semicircle ${activeEmotion === 'dizzy' ? 'is-dizzy' : ''}`}>
          <div className="eyes-container">
            <EyeComponent position={character1EyesPos[0]} emotion={activeEmotion} characterId={1} />
            <EyeComponent position={character1EyesPos[1]} emotion={activeEmotion} characterId={1} />
          </div>
          <MouthComponent emotion={activeEmotion} characterId={1} />
        </div>
      </div>

      {/* Character 2 - Rectangle (Roxo) - Meio, Grande e Alto */}
      <div className="character character-2">
        <div className={`character-body rectangle large ${activeEmotion === 'dizzy' ? 'is-dizzy' : ''}`}>
          <div className="eyes-container">
            <EyeComponent position={character2EyesPos[0]} emotion={activeEmotion} characterId={2} />
            <EyeComponent position={character2EyesPos[1]} emotion={activeEmotion} characterId={2} />
          </div>
          <MouthComponent emotion={activeEmotion} characterId={2} />
        </div>
      </div>

      {/* Character 3 - Rectangle (Preto) - Pequeno, Lado do Grande */}
      <div className="character character-3">
        <div className={`character-body rectangle small ${activeEmotion === 'dizzy' ? 'is-dizzy' : ''}`}>
          <div className="eyes-container">
            <EyeComponent position={character3EyesPos[0]} emotion={activeEmotion} characterId={3} />
            <EyeComponent position={character3EyesPos[1]} emotion={activeEmotion} characterId={3} />
          </div>
          <MouthComponent emotion={activeEmotion} characterId={3} />
        </div>
      </div>

      {/* Character 4 - Cylinder (Amarelo) - Direita */}
      <div className="character character-4">
        <div className={`character-body cylinder ${activeEmotion === 'dizzy' ? 'is-dizzy' : ''}`}>
          <div className="eyes-container">
            <EyeComponent position={character4EyesPos[0]} emotion={activeEmotion} characterId={4} />
            <EyeComponent position={character4EyesPos[1]} emotion={activeEmotion} characterId={4} />
          </div>
          <MouthComponent emotion={activeEmotion} characterId={4} />
        </div>
      </div>
    </div>
  );
}
